/**
 * User Provisioning Management
 * ISO 27001 A.9.2.1 - User registration and de-registration
 *
 * Workflow d'approbation pour la création d'utilisateurs
 * - Simple approbation pour MEMBER, TECHNICIAN, READONLY
 * - Double approbation pour ADMIN, OWNER, ACCOUNTANT, SUPERADMIN
 */

import { useState } from 'react';
import {
  Users,
  UserPlus,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Shield,
  ChevronDown,
  ChevronUp,
  Send,
  X,
} from 'lucide-react';
import { Button, Spinner } from '@/components/ui-v2';
import {
  usePendingProvisioningRequests,
  useMyProvisioningRequests,
  useProvisioningStats,
  useCreateProvisioningRequest,
  useApproveProvisioningRequest,
  useRejectProvisioningRequest,
  useCancelProvisioningRequest,
} from '../hooks/useUserProvisioning';
import type { ProvisioningRequest, UserRole, ProvisioningStatus } from '../api/user-provisioning.api';
import { logger } from '@/utils/logger';

import { useTranslation } from 'react-i18next';

// These will be populated with translated values inside the components
const getRoles = (t: (key: string) => string): { value: UserRole; label: string; privileged: boolean }[] => [
  { value: 'READONLY', label: t('admin.userProvisioning.roleReadonly'), privileged: false },
  { value: 'MEMBER', label: t('admin.userProvisioning.roleMember'), privileged: false },
  { value: 'TECHNICIAN', label: t('admin.userProvisioning.roleTechnician'), privileged: false },
  { value: 'ACCOUNTANT', label: t('admin.userProvisioning.roleAccountant'), privileged: true },
  { value: 'ADMIN', label: t('admin.userProvisioning.roleAdmin'), privileged: true },
  { value: 'OWNER', label: t('admin.userProvisioning.roleOwner'), privileged: true },
];

const getStatusConfig = (t: (key: string) => string): Record<ProvisioningStatus, { label: string; color: string; icon: React.FC<{ className?: string }> }> => ({
  PENDING: { label: t('admin.userProvisioning.statusPending'), color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  APPROVED_L1: { label: t('admin.userProvisioning.statusApprovedL1'), color: 'bg-blue-100 text-blue-700', icon: Clock },
  APPROVED: { label: t('admin.userProvisioning.statusApproved'), color: 'bg-green-100 text-green-700', icon: CheckCircle },
  REJECTED: { label: t('admin.userProvisioning.statusRejected'), color: 'bg-red-100 text-red-700', icon: XCircle },
  EXPIRED: { label: t('admin.userProvisioning.statusExpired'), color: 'bg-gray-100 text-gray-700', icon: AlertTriangle },
  CANCELLED: { label: t('admin.userProvisioning.statusCancelled'), color: 'bg-gray-100 text-gray-500', icon: X },
});

export const UserProvisioning: React.FC = () => {
  const { t } = useTranslation();
  const ROLES = getRoles(t);
  const STATUS_CONFIG = getStatusConfig(t);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedSection, setExpandedSection] = useState<'pending' | 'my' | null>('pending');
  const [selectedRequest, setSelectedRequest] = useState<ProvisioningRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveComment, setApproveComment] = useState('');

  const { data: stats, isLoading: statsLoading } = useProvisioningStats();
  const { data: pendingRequests, isLoading: pendingLoading } = usePendingProvisioningRequests();
  const { data: myRequests, isLoading: myLoading } = useMyProvisioningRequests();

  const approveMutation = useApproveProvisioningRequest();
  const rejectMutation = useRejectProvisioningRequest();
  const cancelMutation = useCancelProvisioningRequest();

  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
    transition-all duration-300`;

  const handleApprove = async (id: string) => {
    try {
      await approveMutation.mutateAsync({ id, comment: approveComment || undefined });
      setSelectedRequest(null);
      setApproveComment('');
    } catch (error) {
      logger.error('Erreur lors de l\'approbation:', error);
    }
  };

  const handleReject = async (id: string) => {
    if (!rejectReason.trim()) return;
    try {
      await rejectMutation.mutateAsync({ id, reason: rejectReason });
      setSelectedRequest(null);
      setRejectReason('');
    } catch (error) {
      logger.error('Erreur lors du rejet:', error);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await cancelMutation.mutateAsync(id);
    } catch (error) {
      logger.error('Erreur lors de l\'annulation:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Users className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('admin.userProvisioning.title')}</h2>
            <p className="text-sm text-gray-500">{t('admin.userProvisioning.subtitle')}</p>
          </div>
        </div>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          variant="secondary"
          icon={<UserPlus className="w-4 h-4" />}
        >
          {t('admin.userProvisioning.newRequest')}
        </Button>
      </div>

      {/* Stats */}
      {!statsLoading && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-yellow-50 rounded-2xl p-4 border border-yellow-200">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-700">{t('admin.userProvisioning.pending')}</span>
            </div>
            <p className="text-2xl font-bold text-yellow-800">{stats.pending}</p>
          </div>
          <div className="bg-green-50 rounded-2xl p-4 border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-700">{t('admin.userProvisioning.approved')}</span>
            </div>
            <p className="text-2xl font-bold text-green-800">{stats.approved}</p>
          </div>
          <div className="bg-red-50 rounded-2xl p-4 border border-red-200">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-5 h-5 text-red-600" />
              <span className="text-sm font-medium text-red-700">{t('admin.userProvisioning.rejected')}</span>
            </div>
            <p className="text-2xl font-bold text-red-800">{stats.rejected}</p>
          </div>
          <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">{t('admin.userProvisioning.expired')}</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{stats.expired}</p>
          </div>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <CreateProvisioningForm onClose={() => setShowCreateForm(false)} />
      )}

      {/* Pending Requests */}
      <div className={cardClasses}>
        <button
          onClick={() => setExpandedSection(expandedSection === 'pending' ? null : 'pending')}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-teal-600" />
            <h3 className="font-semibold text-gray-900">
              {t('admin.userProvisioning.requestsToApprove')} ({pendingRequests?.length || 0})
            </h3>
          </div>
          {expandedSection === 'pending' ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {expandedSection === 'pending' && (
          <div className="mt-4 space-y-4">
            {pendingLoading ? (
              <div className="flex justify-center py-8">
                <Spinner className="w-6 h-6" />
              </div>
            ) : pendingRequests?.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{t('admin.userProvisioning.noPendingRequests')}</p>
            ) : (
              pendingRequests?.map((request) => (
                <RequestCard
                  key={request.id}
                  request={request}
                  onApprove={() => setSelectedRequest(request)}
                  onReject={() => setSelectedRequest(request)}
                  showActions
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* My Requests */}
      <div className={cardClasses}>
        <button
          onClick={() => setExpandedSection(expandedSection === 'my' ? null : 'my')}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <Send className="w-5 h-5 text-teal-600" />
            <h3 className="font-semibold text-gray-900">
              {t('admin.userProvisioning.myRequests')} ({myRequests?.length || 0})
            </h3>
          </div>
          {expandedSection === 'my' ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {expandedSection === 'my' && (
          <div className="mt-4 space-y-4">
            {myLoading ? (
              <div className="flex justify-center py-8">
                <Spinner className="w-6 h-6" />
              </div>
            ) : myRequests?.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{t('admin.userProvisioning.noSentRequests')}</p>
            ) : (
              myRequests?.map((request) => (
                <RequestCard
                  key={request.id}
                  request={request}
                  onCancel={
                    ['PENDING', 'APPROVED_L1'].includes(request.status)
                      ? () => handleCancel(request.id)
                      : undefined
                  }
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* Approval/Rejection Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">{t('admin.userProvisioning.processRequest')}</h3>
              <button
                onClick={() => {
                  setSelectedRequest(null);
                  setApproveComment('');
                  setRejectReason('');
                }}
                className="p-2 hover:bg-gray-100 rounded-xl"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-2xl p-4">
                <p className="text-sm text-gray-500">{t('admin.userProvisioning.requestedUser')}</p>
                <p className="font-medium">{selectedRequest.targetEmail}</p>
                <p className="text-sm text-gray-600">
                  {selectedRequest.targetFirstName} {selectedRequest.targetLastName}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm text-gray-500">{t('admin.userProvisioning.roleLabel')}</span>
                  <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded-lg text-xs font-medium">
                    {ROLES.find(r => r.value === selectedRequest.requestedRole)?.label}
                  </span>
                  {selectedRequest.requiresDoubleApproval && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-lg text-xs font-medium">
                      {t('admin.userProvisioning.doubleValidation')}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.userProvisioning.justification')}
                </label>
                <p className="text-sm text-gray-600 bg-gray-50 rounded-xl p-3">
                  {selectedRequest.justification}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.userProvisioning.commentLabel')}
                </label>
                <textarea
                  value={approveComment}
                  onChange={(e) => setApproveComment(e.target.value)}
                  placeholder={t('admin.userProvisioning.commentPlaceholder')}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.userProvisioning.rejectReason')}
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={t('admin.userProvisioning.rejectReasonPlaceholder')}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => handleApprove(selectedRequest.id)}
                  disabled={approveMutation.isPending}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {approveMutation.isPending ? (
                    <Spinner className="w-4 h-4" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {t('admin.userProvisioning.approve')}
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleReject(selectedRequest.id)}
                  disabled={rejectMutation.isPending || !rejectReason.trim()}
                  variant="secondary"
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {rejectMutation.isPending ? (
                    <Spinner className="w-4 h-4" />
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      {t('admin.userProvisioning.reject')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Request Card Component
const RequestCard: React.FC<{
  request: ProvisioningRequest;
  onApprove?: () => void;
  onReject?: () => void;
  onCancel?: () => void;
  showActions?: boolean;
}> = ({ request, onApprove, onReject, onCancel, showActions }) => {
  const { t } = useTranslation();
  const ROLES = getRoles(t);
  const STATUS_CONFIG = getStatusConfig(t);
  const statusConfig = STATUS_CONFIG[request.status];
  const StatusIcon = statusConfig.icon;

  return (
    <div className="bg-white/50 rounded-2xl p-4 border border-white/50">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium text-gray-900">{request.targetEmail}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
              <StatusIcon className="w-3 h-3 inline mr-1" />
              {statusConfig.label}
            </span>
          </div>
          <div className="text-sm text-gray-600">
            {request.targetFirstName} {request.targetLastName}
          </div>
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
            <span className="px-2 py-1 bg-gray-100 rounded-lg">
              {ROLES.find(r => r.value === request.requestedRole)?.label}
            </span>
            {request.requiresDoubleApproval && (
              <span className="px-2 py-1 bg-orange-50 text-orange-600 rounded-lg">
                {t('admin.userProvisioning.doubleValidationShort')}
              </span>
            )}
            <span>
              {t('admin.userProvisioning.createdAt')} {new Date(request.createdAt).toLocaleDateString('fr-FR')}
            </span>
          </div>
          {request.Requester && (
            <div className="text-xs text-gray-500 mt-1">
              {t('admin.userProvisioning.requestedBy')} {request.Requester.fullName || request.Requester.email}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {showActions && request.canApprove && (
            <>
              <button
                onClick={onApprove}
                className="p-2 bg-green-100 text-green-600 rounded-xl hover:bg-green-200 transition-colors"
                title={t('admin.userProvisioning.approve')}
              >
                <CheckCircle className="w-4 h-4" />
              </button>
              <button
                onClick={onReject}
                className="p-2 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-colors"
                title={t('admin.userProvisioning.reject')}
              >
                <XCircle className="w-4 h-4" />
              </button>
            </>
          )}
          {onCancel && (
            <button
              onClick={onCancel}
              className="p-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors"
              title={t('admin.userProvisioning.cancel')}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Create Provisioning Form
const CreateProvisioningForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  const ROLES = getRoles(t);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<UserRole>('MEMBER');
  const [justification, setJustification] = useState('');

  const createMutation = useCreateProvisioningRequest();

  const selectedRoleInfo = ROLES.find(r => r.value === role);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !justification) return;

    try {
      await createMutation.mutateAsync({
        targetEmail: email,
        targetFirstName: firstName || undefined,
        targetLastName: lastName || undefined,
        requestedRole: role,
        requestedCompanies: [],
        justification,
      });
      onClose();
    } catch (error) {
      logger.error('Erreur lors de la création:', error);
    }
  };

  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-sm`;

  return (
    <div className="bg-gradient-to-r from-teal-50/50 to-white rounded-3xl p-6 border border-teal-200/50">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <UserPlus className="w-5 h-5 text-teal-600" />
          <h3 className="font-semibold text-gray-900">{t('admin.userProvisioning.newRequestTitle')}</h3>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-white rounded-xl">
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('admin.userProvisioning.emailLabel')} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('admin.userProvisioning.placeholders.email')}
              className={inputClasses}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.userProvisioning.firstName')}</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t('admin.userProvisioning.placeholders.firstName')}
              className={inputClasses}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('admin.userProvisioning.lastName')}</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t('admin.userProvisioning.placeholders.lastName')}
              className={inputClasses}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('admin.userProvisioning.requestedRole')} <span className="text-red-500">*</span>
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            className={inputClasses}
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label} {r.privileged && t('admin.userProvisioning.doubleValidationRequired')}
              </option>
            ))}
          </select>
          {selectedRoleInfo?.privileged && (
            <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {t('admin.userProvisioning.doubleValidationNote')}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('admin.userProvisioning.justificationLabel')} <span className="text-red-500">*</span>
          </label>
          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder={t('admin.userProvisioning.justificationPlaceholder')}
            className={inputClasses}
            rows={3}
            required
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            disabled={createMutation.isPending || !email || !justification}
            className="flex-1"
          >
            {createMutation.isPending ? (
              <Spinner className="w-4 h-4" />
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {t('admin.userProvisioning.submitRequest')}
              </>
            )}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UserProvisioning;
